/* eslint-disable max-lines */
// TODO max lines are disabled change this
import { BaseQLClient } from '@adornis/baseql/client/baseqlClient.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/forms/x-plain-input.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { LearningContentAPI } from '@chancenwerk/campus-learning-content/api/LearningContentAPI.js';
import { CampusExercise } from '@chancenwerk/campus-learning-content/entities/CampusExercise.js';
import { CampusLevumiItemMath } from '@chancenwerk/campus-learning-content/entities/levumi/CampusLevumiItemMath.js';
import { LevumiMathCategory } from '@chancenwerk/db/enums/CampusLevumiEnums.js';
import { ExerciseType } from '@chancenwerk/db/enums/enums.js';
import {
  ContentResultEventlog,
  LevumiAnswer,
  LevumiResultInformation,
  getLatestExerciseResultForUser,
} from '@chancenwerk/db/eventlogs/ContentResultEventlog.js';
import '@chancenwerk/design/components/cr-button.js';
import '@chancenwerk/design/components/cr-campus-loader.js';
import '@chancenwerk/design/components/cr-card.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import '@chancenwerk/design/components/cr-flex.js';
import '@chancenwerk/design/components/cr-grid.js';
import '@chancenwerk/design/components/cr-icon.js';
import { type ChromiumInput } from '@chancenwerk/design/components/cr-input.js';
import '@chancenwerk/design/components/fonts.js';
import { type ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { html, type PropertyValueMap } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { filter, of, switchMap, takeUntil } from 'rxjs';
import illuStart from '../../assets/levumi_feedback_start.png';
import '../campus-component-exercise-levumi-summary.js';
import { CampusDialogShowLevumiDifficultyInfo } from '../dialogs/campus-dialog-show-levumi-difficulty-info.js';

@customElement('campus-component-exercise-levumi-math')
export class CampusComponentExerciseLevumiMath extends ChemistryLitElement {
  override styles() {
    return [
      {
        ':host': {
          height: '100%',

          'cr-card': {
            'cr-grid': {
              padding: this.spacing.lg,
              background: 'rgba(175, 202, 0, 0.15)',
              borderRadius: this.spacing.lg,
            },
          },
        },

        '.button': {
          position: 'relative',
          color: 'white',
          padding: '0.8em 1.8em',
          cursor: 'pointer',
          userSelect: 'none',
          textAlign: 'center',
          transitionDuration: '0.4s',
          '-webkit-transition-duration': '0.4s',
          borderRadius: '12px',
          background: '#afca00',
        },
        '.button:hover': {
          backgroundColor: '#afca00',
        },

        '.button:after': {
          display: 'block',
          position: 'absolute',
          borderRadius: '4em',
          left: '0',
          top: '0',
          width: '100%',
          height: '100%',
          opacity: '0',
          transition: 'all 0.5s',
          boxShadow: `0 0 10px 40px ${this.colors.primary}`,
        },

        '.button:active:after': {
          boxShadow: '0 0 0 0 #7011f5',
          position: 'absolute',
          borderRadius: '4em',
          left: '0',
          top: '0',
          opacity: '1',
          transition: '0s',
        },

        '.button:active': {
          top: '1px',
        },

        '@media screen and (max-width: 786px)': {
          'cr-flex.tablet--flex-vertical': {
            flexDirection: 'column-reverse',
          },
          'cr-card cr-grid': {
            display: 'none',
          },
        },
        '@media screen and (max-width: 425px)': {
          'cr-card': {
            '.quest-row': {
              marginLeft: `CALC( 0px - ${this.spacing.lg} - ${this.spacing.xs} )`,
              marginRight: `CALC( 0px - ${this.spacing.lg} - ${this.spacing.xs} )`,

              'x-plain-input': {
                width: `CALC( ${this.spacing.lg} + ${this.spacing.xl} )`,
              },
            },

            'cr-grid': {
              padding: this.spacing.sm,
            },
          },
        },
      },
    ];
  }

  @state() exerciseID = new RXController<string>(this, '');
  @state() private readonly _exercise = new RXController(
    this,
    this.exerciseID.observable.pipe(
      filter(Boolean),
      switchMap(id => CampusExercise.subscribeByID<CampusExercise>(id)(CampusExercise.allFields)),
    ),
  );
  @state() lessonID = new RXController<string>(this, '');

  @state() mode = new RXController<string>(this, 'play');

  @state() private readonly _levumiItems = new RXController(
    this,
    this._exercise.observable.pipe(
      filter(Boolean),
      switchMap(exercise =>
        LearningContentAPI.levumi.subscribeAllMathByLevel(
          exercise.levumiLevel,
          exercise.levumiMathCategory,
        )(CampusLevumiItemMath.allFields),
      ),
    ),
  );

  // States 'ready' | 'intro' | 'play' | 'end'
  @state() private readonly _state = new RXController<string>(this, 'ready');

  @state() private readonly _currentQuestion = new RXController<CampusLevumiItemMath | null>(this, null);

  @state() private readonly _currentAnswer = new RXController<string>(this, '');

  @state() private readonly _currentUser = new RXController(this, currentUser<ChancenwerkUser>());

  @state() private readonly _lastResultEventlog = new RXController(
    this,
    this._currentUser.observable.pipe(
      switchMap(user => {
        if (!user) return of([]);
        return getLatestExerciseResultForUser(user._id, ExerciseType.LEVUMIMATH)(ContentResultEventlog.allFields);
      }),
    ),
  );

  // this variable is only here to try and fix the multiple sending of things
  private _resultSent = false;

  @state() private _loading = false;

  @state() private _currentResultInformation: LevumiResultInformation | undefined;

  private readonly _answeredLevumiItems = new Map<CampusLevumiItemMath, boolean>();

  private _timerEndStamp;
  private _timerStartStamp;
  private _timePlayedInMs;
  private _timerFinished = false;
  private _timer;

  private readonly _currentProbabilitiesForEachCategory = new Map<number, number>();

  private readonly questionsAndPickedAnswers = [] as LevumiAnswer[];

  private _testRoundDone = false;

  private _eventbinded = false;
  private _enterPressed;
  private _tempEventFunction;

  override connectedCallback(): void {
    super.connectedCallback();
    this._tempEventFunction = async function (e: any) {
      if (e.key === 'Enter') {
        if (this._state.value === 'intro') {
          this._initialiseRandomLevumiGenerator();
          await this._saveAnswerAndChooseNext();
          this._state.next('play');
        } else if (this._state.value === 'play') {
          await this._saveAnswerAndChooseNext();
        }
      }
    };
    this._enterPressed = this._tempEventFunction.bind(this);
  }

  override disconnectedCallback(): void {
    document.removeEventListener('keyup', this._enterPressed);
    this._eventbinded = false;
    super.disconnectedCallback();
  }

  override render() {
    return html` <cr-flex center crossaxis-center>
      <cr-card ${css({ borderRadius: '18px', width: '100%' })}>
        <cr-flex space="md" margin="md" center crossaxis-center space-between> ${this._evaluateState()} </cr-flex>
      </cr-card>
    </cr-flex>`;
  }

  override updated(changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    super.updated(changedProperties);

    const regexInput = this.shadowRoot?.getElementById('regexInput');
    if (regexInput) {
      setTimeout(() => {
        regexInput.focus();
        regexInput.addEventListener('value-changed', e => {
          this._currentAnswer.next(e.target.value.value);
        });

        if (!this._eventbinded) {
          document.addEventListener('keyup', this._enterPressed);
          this._eventbinded = true;
        }
      }, 5);
    }
  }

  private _evaluateState() {
    switch (this._state.value) {
      case 'ready':
        return html`${this._showReadyState()}`;
      case 'intro':
        return html`${this._showIntroState()}`;
      case 'play':
        return html`${this._showPlayState()}`;
      case 'end':
        return html`${this._showEndState()}`;
      default:
        return html` <cr-flex space="md" center crossaxis-center> ${this._state.value} </cr-flex> `;
    }
  }

  private _showReadyState() {
    return html`
      <cr-flex space="xl" padding="md">
        <cr-flex class="tablet--flex-vertical" space="md" horizontal crossaxis-center>
          <cr-flex space="md">
            <cr-levumi-text ${css({ fontSize: '1.5rem' })}>
              <span ${css({ fontStyle: 'italic' })}>Bente:</span> Suche dir einen ruhigen Arbeitsort.
            </cr-levumi-text>
            <cr-levumi-text ${css({ fontSize: '1.5rem' })}>
              Du hast jetzt 5 Minuten Zeit, Aufgaben zu bearbeiten.
            </cr-levumi-text>
            <cr-levumi-text ${css({ fontSize: '1.5rem' })}>
              <span ${css({ fontStyle: 'italic' })}>Malu:</span> Bist du bereit?
            </cr-levumi-text>
          </cr-flex>
          <cr-image mode="width" ${css({ width: '50%' })} .value=${illuStart}></cr-image>
        </cr-flex>
        ${this._levumiItems.isLoading
          ? html`<cr-flex space="md" padding="md" center crossaxis-center
              ><cr-campus-loader></cr-campus-loader
            ></cr-flex>`
          : html`
              <cr-flex space="md" center crossaxis-center w100>
                <cr-basic-card
                  mode="card"
                  @click="${() => {
                    this._state.next('intro');
                  }}"
                  ${css({
                    width: 'fit-content',
                    textAlign: 'center',
                    padding: 0,
                    overflow: 'visible',
                    cursor: 'pointer',
                  })}
                >
                  <cr-flex ${css({ borderRadius: '25px' })} space="md" padding="md" center crossaxis-center>
                    <cr-icon ${css({ fontSize: '2rem' })}> play_arrow </cr-icon>
                    <cr-levumi-text> Los geht's! </cr-levumi-text>
                  </cr-flex>
                </cr-basic-card>
              </cr-flex>
              <cr-flex horizontal end>
                <cr-icon
                  ${css({
                    cursor: 'pointer',
                    fontSize: '2rem',
                  })}
                  @click=${async () => {
                    await CampusDialogShowLevumiDifficultyInfo.showPopup({
                      props: {
                        levumiLevel: this._exercise.value?.levumiLevel,
                      },
                    });
                  }}
                >
                  help_outline
                </cr-icon>
              </cr-flex>
            `}
      </cr-flex>
    `;
  }

  private _showIntroState() {
    return html`
      <cr-levumi-text ${css({ fontSize: '2rem' })}> Rechne im Kopf! </cr-levumi-text>
      <cr-flex center crossaxis-center margin="md">
        <cr-levumi-text
          class="quest-row"
          ${css({ lineHeight: this.spacing.xl, fontWeight: '700', textAlign: 'center' })}
          >${unsafeHTML(this._getTextWithBlank())}</cr-levumi-text
        >
      </cr-flex>
      <cr-flex center crossaxis-center space="md"> ${this._showNumpad()} </cr-flex>
      <cr-levumi-text ${css({ fontSize: '1.2rem' })}> Gib die fehlende Zahl ein. </cr-levumi-text>
      <cr-flex center crossaxis-center space="md">
        <cr-button
          ${css({ marginTop: '50px' })}
          @click=${async () => {
            this._initialiseRandomLevumiGenerator();
            await this._saveAnswerAndChooseNext();
            this._state.next('play');
          }}
        >
          Jetzt starten!
        </cr-button>
      </cr-flex>
    `;
  }

  private _showPlayState() {
    return html`
      <cr-flex center crossaxis-center margin="md">
        <cr-levumi-text
          class="quest-row"
          ${css({ lineHeight: this.spacing.xl, fontWeight: '700', textAlign: 'center' })}
          >${unsafeHTML(this._getTextWithBlank())}</cr-levumi-text
        >
      </cr-flex>
      <cr-flex center crossaxis-center margin="md" space="md"> ${this._showNumpad()} </cr-flex>
      <cr-flex center crossaxis-center margin="md" space="md">
        <cr-button
          .loading=${this._loading}
          condensed
          ${css({ marginTop: '50px' })}
          @click="${() => this._saveAnswerAndChooseNext()}"
        >
          Weiter
        </cr-button>
      </cr-flex>
    `;
  }

  private _showEndState() {
    return html`
      <campus-component-exercise-levumi-summary
        .newResult=${this._currentResultInformation?.correctlyAnsweredPercent}
        .prevResult=${this._lastResultEventlog.value?.length &&
        this._lastResultEventlog.value[0]?.levumiResultInformation.correctlyAnsweredPercent}
      ></campus-component-exercise-levumi-summary>
    `;
  }

  private async _saveAnswerAndChooseNext() {
    this._loading = true;
    if (!this._testRoundDone) {
      this._testRoundDone = true;
      this._startTimer();
    } else {
      if (!this._currentQuestion.value) {
        this._loading = false;
        return;
      }
      const answerCorrect = CampusLevumiItemMath.evaluateMathFuntionCorrectness(
        this._currentQuestion.value.mathFunction,
        this._currentAnswer.value as unknown as number,
      );

      this._answeredLevumiItems.set(this._currentQuestion.value, answerCorrect);

      this.questionsAndPickedAnswers.push(
        new LevumiAnswer({
          question: this._currentQuestion.value.mathFunction,
          studentAnswer: this._currentAnswer.value,
          difficulty: this._currentQuestion.value.difficulty,
          answerCorrect,
        }),
      );
    }

    this._currentAnswer.next('');

    if (this._timerFinished) {
      await this._endQuiz();
      return;
    }

    this._currentQuestion.next(await this._getNextRandomLevumiItem());

    this._loading = false;
  }

  private _showNumpad() {
    return html`
      <cr-grid columns="1fr 1fr 1fr">
        ${this._numButton(7)} ${this._numButton(8)} ${this._numButton(9)} ${this._numButton(4)} ${this._numButton(5)}
        ${this._numButton(6)} ${this._numButton(1)} ${this._numButton(2)} ${this._numButton(3)} ${this._numButton(0)}
        <cr-flex
          class="button"
          padding="md"
          margin="sm"
          ${css({ borderRadius: '4px', background: '#afca00', gridColumn: 'span 2', cursor: 'pointer' })}
          @click=${() => {
            const input = this.shadowRoot?.getElementById('regexInput') as ChromiumInput;

            input.value.next(input.value.value === undefined ? '' : input.value.value?.toString().slice(0, -1));

            this._currentAnswer.next(this._currentAnswer.value.toString().slice(0, -1));
          }}
        >
          <cr-text ${css({ color: 'white' })}> Löschen </cr-text>
        </cr-flex>
      </cr-grid>
    `;
  }

  private _numButton(number: number) {
    return html`
      <cr-flex
        class="button"
        padding="md"
        margin="sm"
        @click=${() => {
          const input = this.shadowRoot?.getElementById('regexInput') as ChromiumInput;

          input.value.next(
            input.value.value === null || input.value.value === undefined
              ? number.toString()
              : input.value.value.toString() + number.toString(),
          );
          this._currentAnswer.next(this._currentAnswer.value + number.toString());
        }}
      >
        <cr-text ${css({ color: 'white' })}> ${number} </cr-text>
      </cr-flex>
    `;
  }

  private _initialiseRandomLevumiGenerator() {
    // get all available categories
    for (const levumiItem of this._levumiItems.value ?? []) {
      if (!this._currentProbabilitiesForEachCategory.has(levumiItem.difficulty)) {
        this._currentProbabilitiesForEachCategory.set(levumiItem.difficulty, 0.0);
      }
    }
    const numberOfDifferentCategories = this._currentProbabilitiesForEachCategory.size;
    let index = 1;
    const initialProbability = 1 / numberOfDifferentCategories;

    for (const categoryEntry of this._currentProbabilitiesForEachCategory.entries()) {
      this._currentProbabilitiesForEachCategory.set(categoryEntry[0], initialProbability * index);
      index++;
    }
  }

  private async _getNextRandomLevumiItem(): Promise<CampusLevumiItemMath> {
    const nonPickedItems = this._levumiItems.value?.filter(item => !this._answeredLevumiItems.has(item)) ?? [];

    // Check if all possible Items are answered (should be impossible)
    if (nonPickedItems.length === 0) {
      clearInterval(this._timer);
      await this._endQuiz();
    }

    // Dynamic Probability 1. get the category
    const randomNumber = Math.random();
    let choosenCategory: number | undefined;
    for (const levumiCategory of this._currentProbabilitiesForEachCategory) {
      if (randomNumber < levumiCategory[1]) {
        choosenCategory = levumiCategory[0];
        break;
      }
    }
    // double rounding errors could lead to undefined behavior
    if (!choosenCategory) {
      choosenCategory = Array.from(this._currentProbabilitiesForEachCategory.keys()).pop();
      if (!choosenCategory) throw new Error('Couldnt find any categories');
    }

    this._updateDynamicProbabilities(choosenCategory);

    // Filter the available levumiItems for the choosen category
    let nonPickedItemsForChoosenCategory = nonPickedItems.filter(
      levumiItem => levumiItem.difficulty === choosenCategory,
    );

    // If the category is empty ignore the choosenCategory (should be impossible)
    if (nonPickedItemsForChoosenCategory.length === 0) {
      nonPickedItemsForChoosenCategory = nonPickedItems;
    }

    const newItem =
      nonPickedItemsForChoosenCategory[Math.floor(Math.random() * nonPickedItemsForChoosenCategory.length)];
    if (!newItem)
      throw new Error(
        'Couldnt pick an item. This should not be possible, because we already checked if the item exist!',
      );

    return newItem;
  }

  private _updateDynamicProbabilities(choosenCategory: number) {
    // Dynamic Probability 2. change probabilities: choosen -= changerate | nonChoosen += changerate / categories.lenght - 1
    const changeRate = 0.1;
    const probabilities = Array.from(this._currentProbabilitiesForEachCategory.values());
    const baseProbability = 1 / this._currentProbabilitiesForEachCategory.size;

    // 2.1 Extract non accumulated probabilities
    for (let i = 0; i < probabilities.length; i++) {
      const currentIntervall = probabilities[i];
      if (!currentIntervall) throw new Error('Couldnt find intervall index!');
      probabilities[i] = currentIntervall - baseProbability * i;
    }

    // 2.2 Build new probabilities based on change rate
    let index = 0;
    for (const levumiCategory of this._currentProbabilitiesForEachCategory) {
      if (levumiCategory[0] === choosenCategory) {
        probabilities[index] -= changeRate;
      } else {
        probabilities[index] += changeRate / (this._currentProbabilitiesForEachCategory.size - 1);
      }
      const currentIntervall = probabilities[index];
      if (!currentIntervall) throw new Error('Couldnt find intervall index!');
      this._currentProbabilitiesForEachCategory.set(levumiCategory[0], currentIntervall + baseProbability * index);
      index++;
    }
  }

  private _getTextWithBlank() {
    let question: string;

    if (this._testRoundDone) {
      question = this._currentQuestion.value?.getMathFunctionText() ?? '';
    } else {
      question = this._getIntroQuestion();
    }

    return `
    <cr-flex horizontal crossaxis-center center space="sm">
      ${question.replace(
        CampusLevumiItemMath.blankRegex,
        `<x-plain-input type="number" id="regexInput" autofocus placeholder-mode="hide" style="background: rgba(175, 202, 0, 0.15); border-radius: 32px; border: 0px; font-weight: 700 !important; height: unset; padding-inline: 15px"></x-plain-input>`,
      )}
      </cr-flex>
    `;
  }

  private _getIntroAdditionQuestion() {
    switch (this._exercise.value?.levumiLevel.level) {
      case 'Station 1':
        return '52 + $ = 54';
      case 'Station 2':
        return '32 + $ = 54';
      case 'Station 3':
        return '49 + 2 = $';
      case 'Station 4':
        return '19 + $ = 34';
      default:
        return '1 + 1 = $';
    }
  }

  // TODO change these
  private _getIntroSubtractionQuestion() {
    switch (this._exercise.value?.levumiLevel.level) {
      case 'Station 1':
        return '1 - $ = 0';
      case 'Station 2':
        return '1 - $ = 0';
      case 'Station 3':
        return '1 - $ = 0';
      case 'Station 4':
        return '1 - $ = 0';
      default:
        return '1 - 1 = $';
    }
  }

  // TODO change these
  private _getIntroMutationQuestion() {
    switch (this._exercise.value?.levumiLevel.level) {
      case 'Station 1':
        return '1 ⋅ 2 = $';
      case 'Station 2':
        return '1 ⋅ 2 = $';
      case 'Station 3':
        return '1 ⋅ 2 = $';
      case 'Station 4':
        return '1 ⋅ 2 = $';
      case 'Station 5':
        return '1 ⋅ 2 = $';
      case 'Station 6':
        return '1 ⋅ 2 = $';
      default:
        return '1 ⋅ 2 = $';
    }
  }

  private _getIntroQuestion() {
    switch (this._exercise.value?.levumiMathCategory) {
      case LevumiMathCategory.ADD:
        return this._getIntroAdditionQuestion();
      case LevumiMathCategory.SUB:
        return this._getIntroSubtractionQuestion();
      case LevumiMathCategory.MUL:
        return this._getIntroMutationQuestion();
      default:
        return '1 + 1 = $';
    }
  }

  private _startTimer() {
    this._timerStartStamp = new Date().getTime();
    this._timerEndStamp = new Date().getTime() + 300000; // ms for: 5 minutes = 300000 | 10 seconds = 10000
    this._timer = setInterval(() => {
      const now = new Date().getTime();
      const delta = this._timerEndStamp - now;

      this._timePlayedInMs = now - this._timerStartStamp;

      if (delta <= 0) {
        clearInterval(this._timer);
        this._timerFinished = true;
        return;
      }
      if (delta <= 10000) {
        const el = this.shadowRoot?.getElementById('timer');
        if (el) el.style.color = 'red';
      }

      const minutes = Math.floor((delta % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((delta % (1000 * 60)) / 1000);
      const el = this.shadowRoot?.getElementById('timer');
      if (el) el.innerHTML = minutes + ':' + seconds;
    }, 1000);
  }

  private async _endQuiz() {
    await ChromiumDialog.waitFor(
      new Promise<void>((resolve, reject) => {
        BaseQLClient.isConnected()
          .pipe(takeUntil(this.disconnected))
          .subscribe(connected => {
            if (connected) resolve();
          });
      }),
    );
    await ChromiumDialog.waitFor(
      this._submitResults(),
      'Die Zeit ist abgelaufen. Dein Ergebnis wird gerade gespeichert. 🌞',
    );
    this._state.next('end');
    this._testRoundDone = false;
  }

  private async _submitResults() {
    if (this._resultSent) return;
    this._resultSent = true;
    if (this.mode.value === 'preview') return;
    this._currentResultInformation = this._getLevumiResult();
    await LearningContentAPI.levumi.submitLevumiExercise(
      this._currentUser.value?._id ?? '',
      this.exerciseID.value || '',
      this.lessonID.value || '',
      this._currentResultInformation,
    )();
  }

  private _getLevumiResult() {
    const correctlyAnswered = this._getCorrectAnsweredQuestions().length;
    return new LevumiResultInformation({
      levumiLevel: this._exercise.value?.levumiLevel,
      levumiMathCategory: this._exercise.value?.levumiMathCategory,
      overallAnswered: this._answeredLevumiItems.size,
      questionsAndAnswers: this.questionsAndPickedAnswers,
      correctlyAnswered,
      correctlyAnsweredPercent: correctlyAnswered / this._answeredLevumiItems.size,
      readingAccuracyInPercent: (correctlyAnswered * 100) / this._answeredLevumiItems.size,
      readingSpeedInQuePerMin: this._answeredLevumiItems.size / (this._timePlayedInMs / 1000 / 60),
    });
  }

  private _getCorrectAnsweredQuestions() {
    const questions = Array.from(this._answeredLevumiItems.entries());
    const correct = questions.filter(pair => pair[1]).map(pair => pair[0]);
    return correct;
  }
}
